import { useState, useEffect } from 'react';
import { History, ChevronLeft, ChevronRight, MessageSquare, ExternalLink, Layers } from 'lucide-react';
import { getHistory, ClassificationResult } from '../services/api';
import FeedbackModal from '../components/FeedbackModal';
import toast from 'react-hot-toast';

type SourceFilter = 'all' | 'single' | 'batch';

export default function HistoryPage() {
    const [classifications, setClassifications] = useState<ClassificationResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [source, setSource] = useState<SourceFilter>('all');
    const [selectedItem, setSelectedItem] = useState<ClassificationResult | null>(null);

    const loadHistory = async (p: number, src: SourceFilter) => {
        setLoading(true);
        try {
            const data = await getHistory(p, 15, src);
            setClassifications(data.data);
            setTotalPages(data.pagination.total_pages);
            setTotal(data.pagination.total);
        } catch {
            toast.error('加载历史记录失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadHistory(page, source); }, [page, source]);

    const handleSourceChange = (s: SourceFilter) => {
        setSource(s);
        setPage(1);
    };

    const confidenceColor = (c: number) => {
        if (c >= 80) return 'text-emerald-400';
        if (c >= 50) return 'text-amber-400';
        return 'text-red-400';
    };

    const formatDate = (dateStr: string) =>
        new Intl.DateTimeFormat('zh-CN', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
        }).format(new Date(dateStr));

    return (
        <div className="p-6 max-w-5xl mx-auto animate-fade-in">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                    <History size={20} className="text-brand-400" />
                    <h1 className="text-xl font-bold text-white">分类历史</h1>
                    <span className="badge badge-blue ml-1">{total} 条记录</span>
                </div>
                <p className="text-slate-400 text-sm">查看所有分类历史记录（单品 + 批量），可对错误分类进行反馈纠正</p>
            </div>

            {/* Source filter tabs */}
            <div className="flex gap-1 mb-4 bg-surface-2 rounded-xl p-1 w-fit">
                {([
                    { key: 'all', label: '全部' },
                    { key: 'single', label: '单品' },
                    { key: 'batch', label: '批量' },
                ] as { key: typeof source; label: string }[]).map((tab) => (
                    <button
                        key={tab.key}
                        onClick={() => handleSourceChange(tab.key)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            source === tab.key
                                ? 'bg-brand-600 text-white shadow'
                                : 'text-slate-400 hover:text-white'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="card p-0 overflow-hidden">
                {loading ? (
                    <div className="p-8 space-y-3">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="h-14 bg-white/[0.03] rounded-xl animate-pulse" />
                        ))}
                    </div>
                ) : classifications.length === 0 ? (
                    <div className="flex flex-col items-center py-20 text-slate-600">
                        <History size={40} className="mb-3 opacity-30" />
                        <p className="font-medium text-slate-500">暂无分类记录</p>
                        <p className="text-sm mt-1">前往「AI 分类」页面开始对产品进行分类</p>
                    </div>
                ) : (
                    <>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-white/[0.06]">
                                    {['来源', '时间', '产品描述', 'HTS 编码', '置信度', '基础税率', '操作'].map((h) => (
                                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {classifications.map((item: ClassificationResult & { created_at?: string; source?: string; product_description?: string }) => (
                                    <tr key={item.id} className="border-b border-white/[0.04] table-row-hover">
                                        <td className="px-4 py-3">
                                            {item.source === 'batch' ? (
                                                <span className="inline-flex items-center gap-1 badge badge-blue text-xs">
                                                    <Layers size={10} />批量
                                                </span>
                                            ) : (
                                                <span className="badge badge-green text-xs">单品</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                                            {item.created_at ? formatDate(item.created_at) : '—'}
                                        </td>
                                        <td className="px-4 py-3 text-slate-300 max-w-xs">
                                            <p className="line-clamp-2 text-xs leading-relaxed">{item.product_description || '—'}</p>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5">
                                                <span className="hts-code">{item.hts_code_formatted}</span>
                                                <a
                                                    href={`https://hts.usitc.gov/?query=${item.hts_code}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-slate-600 hover:text-brand-400 transition-colors"
                                                >
                                                    <ExternalLink size={11} />
                                                </a>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-sm font-semibold ${confidenceColor(item.confidence)}`}>
                                                {item.confidence}%
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-amber-400 text-xs font-mono">
                                            {item.duty_rate != null && item.duty_rate > 0
                                                ? `${(item.duty_rate * 100).toFixed(1)}%`
                                                : <span className="text-slate-600">0% / 免税</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => setSelectedItem(item)}
                                                className="text-slate-600 hover:text-brand-400 transition-colors"
                                                title="提交纠错"
                                            >
                                                <MessageSquare size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
                                <p className="text-xs text-slate-500">
                                    第 {page} 页，共 {totalPages} 页 · 共 {total} 条
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="btn-secondary px-3 py-1.5 text-sm"
                                    >
                                        <ChevronLeft size={14} />
                                    </button>
                                    <button
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="btn-secondary px-3 py-1.5 text-sm"
                                    >
                                        <ChevronRight size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {selectedItem && (
                <FeedbackModal
                    classification={selectedItem}
                    onClose={() => { setSelectedItem(null); loadHistory(page, source); }}
                />
            )}
        </div>
    );
}
